import { type AtomEffect, DefaultValue } from 'recoil';

import { createCache } from '@gitbook/util-suspense-cache';

import { Logger } from '../../util-debug/src';
import type { LocalStorage } from './types';

const logger = Logger('app:localStorage');

/**
 * Robust way to test for localStorage functionality, not just existence.
 * See https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
 */
let localStorageAvailable: boolean | null = null;

const isLocalStorageAvailable = (): boolean => {
    // Return cached result if we've already checked
    if (localStorageAvailable !== null) {
        return localStorageAvailable;
    }

    try {
        const storage = window.localStorage;
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        localStorageAvailable = true;
        return true;
    } catch (e) {
        const available =
            e instanceof DOMException &&
            e.name === 'QuotaExceededError' &&
            // acknowledge QuotaExceededError only if there's something already stored
            window.localStorage &&
            window.localStorage.length !== 0;
        localStorageAvailable = available;
        return available;
    }
};

/**
 * Wrapper to safely execute localStorage operations with fallback when localStorage is unavailable
 */
function withLocalStorageCheck<T>(operation: () => T): T | null;
function withLocalStorageCheck<T>(operation: () => Promise<T>): Promise<T | null>;
function withLocalStorageCheck<T>(operation: () => T | Promise<T>): T | null | Promise<T | null> {
    if (!isLocalStorageAvailable()) {
        logger.error('localStorage is not available or functional, skipping operation');
        return null;
    }
    return operation();
}

const storageCache = createCache<string, any>({
    getKey: (key) => key,
    getValue: (key) => {
        return withLocalStorageCheck(() => {
            const rawValue = window.localStorage.getItem(key);

            if (rawValue === null) {
                return rawValue;
            }

            try {
                return JSON.parse(rawValue);
            } catch (_err) {
                // Couldn't parse the JSON value, something went wrong.
                return null;
            }
        });
    },
});

/**
 * Interface to interact with localStorage, backed by a suspense cache.
 *
 * We need a web specific implementation to avoid suspending the rendering
 * when accessing the localStorage.
 */
export const localStorage: LocalStorage = {
    async set(key: string, object: any) {
        return withLocalStorageCheck(async () => {
            window.localStorage.setItem(key, JSON.stringify(object));
            storageCache.write(key, object);
        });
    },

    async remove(key: string) {
        return withLocalStorageCheck(async () => {
            window.localStorage.removeItem(key);
            storageCache.clear(key);
        });
    },

    get(key: string) {
        return withLocalStorageCheck(() => {
            return storageCache.read(key);
        });
    },

    async getAsync(key: string) {
        return withLocalStorageCheck(async () => {
            return storageCache.readAsync(key);
        });
    },

    recoilEffect<T>(key: string, defaultValue: T): AtomEffect<T> {
        return ({ trigger, setSelf, onSet }) => {
            if (typeof window === 'undefined') {
                return;
            }
            if (trigger === 'get') {
                const result = localStorage.get<T>(key);
                if (result !== null) {
                    setSelf(result);
                } else {
                    setSelf(defaultValue);
                }
            }

            onSet((newValue: T | DefaultValue) => {
                if (newValue instanceof DefaultValue) {
                    return localStorage.remove(key);
                }
                return localStorage.set(key, newValue);
            });
        };
    },

    clear() {
        return withLocalStorageCheck(() => {
            window.localStorage.clear();
        });
    },
};
